const DRAFT_KEY = 'rb_intake'
const PENDING_KEY = 'rb_intake_pending'

export const INTAKE_TOTAL_STEPS = 9

export const PARTICIPANT_KEYS = ['SELF', 'COUPLE', 'CHILD', 'TEEN', 'PARENT', 'FAMILY']

const CHILD_AGE_KEYS = ['CHILD', 'TEEN', 'PARENT']

export function usesChildAge(participant) {
  return CHILD_AGE_KEYS.indexOf(participant) !== -1
}

export function loadIntake() {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY)
    if (!raw) {
      return emptyIntake()
    }
    const data = JSON.parse(raw)
    return {
      participant: normalizeParticipant(data.participant || 'SELF'),
      age: data.age == null ? '' : data.age,
      agePartner: data.agePartner == null ? '' : data.agePartner,
      topics: Array.isArray(data.topics) ? data.topics : [],
      primaryTopic: data.primaryTopic || '',
      topicsLinked: data.topicsLinked || '',
      u1Domains: Array.isArray(data.u1Domains) ? data.u1Domains : [],
      u2Experience: data.u2Experience || '',
      u3Closeness: data.u3Closeness || '',
      intensity: data.intensity == null ? '' : data.intensity,
      frequency: data.frequency || '',
      functionDifficulty: data.functionDifficulty || '',
      recentTrend: data.recentTrend || '',
      historyDuration: data.historyDuration || '',
      priorHelp: Array.isArray(data.priorHelp) ? data.priorHelp : [],
      priorOutcome: data.priorOutcome || '',
      currentCare: Array.isArray(data.currentCare) ? data.currentCare : [],
      needsCoordination: data.needsCoordination || '',
      involvedDomains: Array.isArray(data.involvedDomains) ? data.involvedDomains : [],
      goalAlignment: data.goalAlignment || '',
      caregiverAvailable: data.caregiverAvailable || '',
      branchQueue: Array.isArray(data.branchQueue) ? data.branchQueue : [],
      branchAnswers: data.branchAnswers && typeof data.branchAnswers === 'object'
        ? data.branchAnswers
        : {},
      clinical: data.clinical && typeof data.clinical === 'object'
        ? data.clinical
        : {},
      safetyScreen: data.safetyScreen && typeof data.safetyScreen === 'object'
        ? data.safetyScreen
        : null,
      severity: data.severity || '',
      duration: data.duration || '',
      impact: data.impact || '',
      trend: data.trend || '',
      safety: Array.isArray(data.safety) ? data.safety : [],
      availabilitySlots: Array.isArray(data.availabilitySlots) ? data.availabilitySlots : [],
      flexibility: data.flexibility || '',
      livedExperience: Array.isArray(data.livedExperience) ? data.livedExperience : [],
      livedExperienceNeed: data.livedExperienceNeed || '',
      days: Array.isArray(data.days) ? data.days : [],
      times: Array.isArray(data.times) ? data.times : [],
      gender: data.gender || '',
      therapistId: data.therapistId || '',
      bookingDate: data.bookingDate || '',
      bookingTime: data.bookingTime || '',
      bookingDateLabel: data.bookingDateLabel || '',
      holdStartedAt: data.holdStartedAt || '',
      paymentMethod: data.paymentMethod || '',
      bookingCode: data.bookingCode || '',
      receiptCode: data.receiptCode || '',
      paymentOutcome: data.paymentOutcome || '',
      followupRequested: !!data.followupRequested,
    }
  } catch (e) {
    return emptyIntake()
  }
}

export function saveIntake(patch) {
  const next = Object.assign({}, loadIntake(), patch)
  ;['consentAccepted','consentSignature','consentAt','consentVersion','readiness','baselineOrs','baselineComplete','letter','letterComplete','letterVoiceLocal','consentSignerReviewed','consentGuardSubmitted'].forEach((key) => delete next[key])
  sessionStorage.setItem(DRAFT_KEY, JSON.stringify(next))
  return next
}

export function markIntakePending() {
  sessionStorage.setItem(PENDING_KEY, '1')
}

export function hasIntakePending() {
  return sessionStorage.getItem(PENDING_KEY) === '1'
}

export function clearIntakePending() {
  sessionStorage.removeItem(PENDING_KEY)
}

/** INTAKE-02 need catalog (same for all participant paths in the HTML). */
export const UNSURE_TOPIC = 'unsure'
export const MAX_TOPICS = 6

/** U1 life domains (unsure path). */
export const U1_DOMAIN_KEYS = ['mood', 'body', 'rel', 'work', 'other']

/** U2 experience options → catalog topic when confirmed on U3. */
export const U2_EXPERIENCE_KEYS = ['a', 'b', 'c', 'd']

export const U2_TO_TOPIC = {
  a: 'anxiety_worry',
  b: 'mood_depression',
  c: 'relationship_conflict',
  d: UNSURE_TOPIC,
}

export const U3_CLOSENESS_KEYS = ['yes', 'partial', 'no']

export function mapU2ToTopic(u2) {
  return U2_TO_TOPIC[u2] || UNSURE_TOPIC
}

export const NEED_TOPIC_GROUPS = [
  {
    id: 'mood',
    chips: ['mood_depression', 'mood_swing', 'mood_anhedonia'],
  },
  {
    id: 'anxiety',
    chips: ['anxiety_worry', 'anxiety_panic', 'anxiety_phobia'],
  },
  {
    id: 'trauma',
    chips: ['trauma_grief', 'trauma_event', 'trauma_flashback'],
  },
  {
    id: 'relationship',
    chips: ['relationship_conflict', 'relationship_trust', 'relationship_loneliness'],
  },
  {
    id: 'family',
    chips: ['family_conflict', 'family_boundaries', 'family_caregiving'],
  },
  {
    id: 'work',
    chips: ['work_burnout', 'work_stress', 'work_study'],
  },
  {
    id: 'sleep',
    chips: ['sleep_insomnia', 'sleep_hypersomnia', 'sleep_nightmare'],
  },
  {
    id: 'substance',
    chips: ['substance_alcohol', 'substance_drugs', 'substance_compulsive'],
  },
  {
    id: 'identity',
    chips: ['identity_confusion', 'identity_esteem', 'identity_meaning'],
  },
  {
    id: 'body',
    chips: ['body_pain', 'body_image', 'body_illness'],
  },
]

export function allNeedTopicIds() {
  const ids = []
  NEED_TOPIC_GROUPS.forEach((group) => {
    group.chips.forEach((id) => ids.push(id))
  })
  return ids
}

export function topicsFor() {
  return allNeedTopicIds()
}

export function isUnsureTopics(topics) {
  return Array.isArray(topics) && topics.length === 1 && topics[0] === UNSURE_TOPIC
}

export function toFaDigits(value) {
  return String(value).replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[digit])
}

function normalizeParticipant(value) {
  if (value === 'TOGETHER') {
    return 'TEEN'
  }
  return value
}

function emptyIntake() {
  return {
    participant: 'SELF',
    age: '',
    agePartner: '',
    topics: [],
    primaryTopic: '',
    topicsLinked: '',
    u1Domains: [],
    u2Experience: '',
    u3Closeness: '',
    intensity: '',
    frequency: '',
    functionDifficulty: '',
    recentTrend: '',
    historyDuration: '',
    priorHelp: [],
    priorOutcome: '',
    currentCare: [],
    needsCoordination: '',
    involvedDomains: [],
    goalAlignment: '',
    caregiverAvailable: '',
    branchQueue: [],
    branchAnswers: {},
    clinical: {},
    safetyScreen: null,
    severity: '',
    duration: '',
    impact: '',
    trend: '',
    safety: [],
    availabilitySlots: [],
    flexibility: '',
    livedExperience: [],
    livedExperienceNeed: '',
    days: [],
    times: [],
    gender: '',
    therapistId: '',
    bookingDate: '',
    bookingTime: '',
    bookingDateLabel: '',
    holdStartedAt: '',
    paymentMethod: '',
    bookingCode: '',
    receiptCode: '',
    paymentOutcome: '',
    followupRequested: false,
  }
}

export const CONNECTED_TOPIC = 'CONNECTED'

export const LINKED_KEYS = ['together', 'separate', 'unsure']

export const INTENSITY_VALUES = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']

export const FREQUENCY_KEYS = ['NEVER', 'FEW_DAYS', 'MORE_THAN_HALF', 'ALMOST_DAILY']

export const FUNCTION_KEYS = ['NONE', 'LOW', 'MEDIUM', 'HIGH']

export const RECENT_TREND_KEYS = ['BETTER', 'SAME', 'WORSE', 'VOLATILE']

export const HISTORY_DURATION_KEYS = ['LT_1M', 'M1_3', 'M3_12', 'GT_1Y', 'CHILDHOOD']

export const PRIOR_HELP_KEYS = ['none', 'therapy', 'psych', 'other']

export const PRIOR_OUTCOME_KEYS = ['helped', 'mixed', 'not', 'na']

export const CURRENT_CARE_KEYS = ['none', 'therapist', 'psychiatrist', 'doctor']

export const COORDINATION_KEYS = ['yes', 'no', 'unsure']

export const INVOLVED_DOMAIN_KEYS = ['work', 'home', 'health', 'others', 'none']

export const GOAL_ALIGNMENT_KEYS = ['aligned', 'partial', 'not', 'self_only']

export const CAREGIVER_KEYS = ['yes', 'no', 'na']

export function isHistoryComplete(draft) {
  return !!(
    draft &&
    draft.historyDuration &&
    Array.isArray(draft.priorHelp) && draft.priorHelp.length &&
    draft.priorOutcome &&
    Array.isArray(draft.currentCare) && draft.currentCare.length &&
    draft.needsCoordination &&
    Array.isArray(draft.involvedDomains) && draft.involvedDomains.length &&
    draft.goalAlignment &&
    draft.caregiverAvailable
  )
}

export const SEVERITY_KEYS = ['LOW', 'MEDIUM', 'HIGH', 'VERY_HIGH']

export const DURATION_KEYS = ['WEEKS', 'MONTHS_1_6', 'MONTHS_6_12', 'YEARS']

export const IMPACT_KEYS = ['ONE', 'SEVERAL', 'MOST']

export const TREND_KEYS = ['BETTER', 'SAME', 'WORSE']

export const SAFETY_NONE = 'NONE'

export const SAFETY_KEYS = [
  'SELF_HARM',
  'HARM_OTHER',
  'VIOLENCE',
  'PSYCHOSIS',
  'MANIA',
  'MEDICAL',
  SAFETY_NONE,
]

export const YES_NO_KEYS = ['yes', 'no']

export const SAFETY_LAST_TIME_KEYS = ['3m', 'year', 'older', 'na']

export const SAFETY_ACUTE_KEYS = ['none', 'mania', 'psychosis', 'intox', 'medical']

export const SAFETY_DOMESTIC_KEYS = ['no', 'yes', 'unsure']

export function emptySafetyScreen() {
  return {
    wishDead: '',
    ideation: '',
    method: '',
    intent: '',
    plan: '',
    behavior: '',
    lastTime: '',
    acuteStates: [],
    domesticThreat: '',
    coupleFear: '',
    coupleHarm: '',
    familyFear: '',
    familyConcern: '',
    youthTalk: '',
    youthConcern: '',
  }
}

export function isCoreSafetyFlagged(screen) {
  if (!screen) {
    return false
  }
  return screen.ideation === 'yes' || screen.domesticThreat === 'yes'
}

export function isSafetyFlagged(screen) {
  if (!screen) {
    return false
  }
  if (isCoreSafetyFlagged(screen)) {
    return true
  }
  if (
    screen.coupleFear === 'yes' ||
    screen.coupleHarm === 'yes' ||
    screen.familyFear === 'yes' ||
    screen.familyConcern === 'yes' ||
    screen.youthTalk === 'yes' ||
    screen.youthConcern === 'yes'
  ) {
    return true
  }
  return (screen.acuteStates || []).some((item) => item !== 'none')
}

export function nextAfterCoreSafety(participant) {
  if (participant === 'COUPLE') {
    return 'intakeSafeCouple'
  }
  if (participant === 'FAMILY') {
    return 'intakeSafeFamily'
  }
  if (participant === 'CHILD' || participant === 'TEEN' || participant === 'PARENT') {
    return 'intakeSafeYouth'
  }
  return 'intakeAvailability'
}

export function hasSafetyRisk(safety) {
  return (safety || []).some((item) => item !== SAFETY_NONE)
}

export const DAY_KEYS = ['SAT', 'SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI']

export const TIME_KEYS = ['MORNING', 'NOON', 'AFTERNOON', 'EVENING']

export const SLOT_KEYS = TIME_KEYS

export const FLEXIBILITY_KEYS = ['low', 'mid', 'high']

export const GENDER_KEYS = ['FEMALE', 'MALE', 'NO_PREFERENCE']

export const GENDER_PREF_KEYS = ['any', 'woman', 'man']

export const LIVED_EXPERIENCE_KEYS = ['none', 'lgbt', 'faith', 'chronic', 'migration']

export const LIVED_NEED_KEYS = ['soft', 'hard', 'na']

export function deriveDaysTimesFromSlots(slots) {
  const days = []
  const times = []
  ;(slots || []).forEach((id) => {
    const parts = String(id).split('/')
    if (parts.length !== 2) {
      return
    }
    if (DAY_KEYS.indexOf(parts[0]) !== -1 && days.indexOf(parts[0]) === -1) {
      days.push(parts[0])
    }
    if (SLOT_KEYS.indexOf(parts[1]) !== -1 && times.indexOf(parts[1]) === -1) {
      times.push(parts[1])
    }
  })
  return {days, times}
}

export function progressPercent(step) {
  return Math.floor((step / INTAKE_TOTAL_STEPS) * 100) + '%'
}

export function formatFaAmount(value) {
  const grouped = String(value).replace(/\B(?=(\d{3})+(?!\d))/g, '٬')
  return toFaDigits(grouped)
}

export function isIntakeReadyToMatch(draft) {
  if (!draft) {
    return false
  }
  const hasSlots = Array.isArray(draft.availabilitySlots) && draft.availabilitySlots.length
  const hasLegacy = draft.days && draft.days.length && draft.times && draft.times.length
  return !!(draft.gender && (hasSlots || hasLegacy))
}
